import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';

// StereoRender — side-by-side output for a Cardboard-style viewer.
//
// This is the difference between "look around with your phone" and VR: two
// images, one per eye, offset by the interpupillary distance so the brain fuses
// them into depth. Drop the phone into a £15 holder and the room has volume.
//
// ── WHY IT TAKES OVER THE RENDER LOOP ────────────────────────────────────────
// r3f renders once per frame from one camera. Stereo needs two passes with
// different projection matrices into two halves of the canvas, which cannot be
// expressed as a scene graph change. So the loop is disabled (frameloop is left
// alone; instead we render manually at priority 1000, after every other
// useFrame, and r3f's own render is suppressed by taking the render callback
// over) — the standard three.js StereoEffect approach.
//
// NOT WebXR. WebXR would be better on a real headset and gives you proper
// reprojection, but it needs a headset or an Android device with ARCore, cannot
// be tested on iOS Safari at all, and would not run on the phone in your pocket.
// This runs anywhere `deviceorientation` does, which is the point of the
// exercise: something testable today.
//
// The eye separation is in WORLD units. This world is ~2.2m per unit, and a
// human IPD is ~64mm, so 0.064/2.2 ≈ 0.029. Getting this wrong is the other
// classic way to make people ill: too wide and the world reads as a doll's
// house, too narrow and depth collapses.
const IPD_METRES = 0.064;
const METRES_PER_UNIT = 2.2;
// Live knobs, because both of these are felt rather than calculated and the
// difference between "solid" and "queasy" is small. ?ipd=0.02 ?eyeaspect=0.5
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) && v > 0 ? v : d;
};

// MOUNTED ONLY WHILE STEREO IS ON — see PlanetScene. That is not tidiness: the
// priority below is what hands rendering to this component, and a registered
// callback at priority 1000 disables r3f's own render whether or not the body
// does anything. Left mounted with stereo off, the screen goes black and stays
// black, which is a spectacular way to break the whole world with a feature
// nobody switched on.
export default function StereoRender() {
  const { gl, scene, camera, size } = useThree();
  const eyes = useMemo(() => new THREE.StereoCamera(), []);
  // How far inward each eye's picture moves so it sits under the lens rather
  // than in the middle of its half. See worldStore.eyeShift / world.lens().
  const eyeShift = useWorldStore((s) => s.eyeShift);

  useEffect(() => {
    // ── HALF THE WIDTH MEANS HALF THE ASPECT ─────────────────────────────────
    // StereoCamera projects each eye at `camera.aspect * this.aspect`, and
    // `this.aspect` defaults to 1 — the FULL canvas aspect. Each eye is then
    // drawn into half the canvas width, squeezing the image 2× horizontally.
    //
    // The result is not a valid stereo pair: the two views no longer correspond
    // to two eyes looking at one scene, so they refuse to fuse and read as a
    // doubled image rather than as depth. It looks like an eye-separation
    // problem, which is where you would go looking, and it is not.
    eyes.aspect = qp('eyeaspect', 0.5);
    eyes.eyeSep = qp('ipd', IPD_METRES / METRES_PER_UNIT);
    const prevAuto = gl.autoClear;
    return () => {
      // Handing the loop back: restore what we changed, or the mono view comes
      // back scissored to the left half.
      gl.autoClear = prevAuto;
      gl.setScissorTest(false);
      gl.setViewport(0, 0, size.width, size.height);
    };
  }, [gl, eyes, size.width, size.height]);

  useFrame(() => {
    if (camera.parent === null) camera.updateMatrixWorld();
    eyes.update(camera);

    // Scissor/viewport are in DRAWING-BUFFER pixels, not CSS pixels — on a phone
    // at dpr 2 the CSS size is half the buffer, so using it would render both
    // eyes into the bottom-left quarter of the canvas.
    const dpr = gl.getPixelRatio();
    const w = size.width * dpr;
    const h = size.height * dpr;
    gl.setScissorTest(true);
    gl.autoClear = false;
    gl.clear();

    // ── SCISSOR STAYS PUT; VIEWPORT MOVES ────────────────────────────────────
    // The scissor is WHERE PIXELS MAY LAND — still an exact half, so the two
    // eyes never bleed into each other. The viewport is HOW THE IMAGE MAPS ONTO
    // THEM, so sliding it inward slides the picture's centre onto the lens axis.
    // The far edge of each half falls outside the viewport and stays black; it
    // is beyond the lens's usable circle and was never visible anyway.
    //
    // Doing it here rather than by patching the projection matrix on purpose:
    // StereoCamera builds an asymmetric frustum per eye and caches it, so a
    // per-frame write to its matrix would either be clobbered on the next
    // rebuild or accumulate. The viewport is stateless every frame.
    const shift = eyeShift * dpr;

    // LEFT half — picture slides RIGHT, toward the seam.
    gl.setScissor(0, 0, w / 2, h);
    gl.setViewport(shift, 0, w / 2, h);
    gl.render(scene, eyes.cameraL);

    // RIGHT half — picture slides LEFT, toward the seam.
    gl.setScissor(w / 2, 0, w / 2, h);
    gl.setViewport(w / 2 - shift, 0, w / 2, h);
    gl.render(scene, eyes.cameraR);

    gl.setScissorTest(false);
    gl.setViewport(0, 0, w, h);
  }, 1000);

  return null;
}
